import { hasSeenTour, type OnboardingFlags } from './flags';
import { tourSelector } from './targets';
import type { TourAudienceContext, TourDefinition, TourPlacement, TourStep } from './tourTypes';

/**
 * Matches TanStack-style route patterns: `$param` matches one segment and a
 * trailing `*` matches the rest of the path.
 */
export function matchRoute(pattern: string, pathname: string): boolean {
  const clean = (p: string) => p.replace(/\/+$/, '') || '/';
  const pat = clean(pattern).split('/');
  const path = clean(pathname).split('/');
  for (let i = 0; i < pat.length; i++) {
    const seg = pat[i];
    if (seg === '*') return i < path.length && !!path[i];
    if (i >= path.length) return false;
    if (seg.startsWith('$')) {
      if (!path[i]) return false;
      continue;
    }
    if (seg !== path[i]) return false;
  }
  return pat.length === path.length;
}

/** The CSS selector for a step, or `null` for a centred card. */
export function resolveStepSelector(step: TourStep, isMobile: boolean): string | null {
  if (isMobile && step.mobileTarget !== undefined) {
    return step.mobileTarget ? tourSelector(step.mobileTarget) : null;
  }
  return step.target ? tourSelector(step.target) : null;
}

/**
 * Whether the step needs a navigation first, and where to. A query in the step
 * route (e.g. `/profile/my-gigs?tab=offers`) must match too; pattern routes
 * can't be navigated to, so the step just waits for its element.
 */
export function stepNavigation(step: TourStep, pathname: string, search = ''): string | null {
  if (!step.route) return null;
  const [path, query] = step.route.split('?');
  if (!matchRoute(path, pathname)) return /[$*]/.test(path) ? null : step.route;
  if (!query) return null;
  const have = new URLSearchParams(search);
  for (const [name, value] of new URLSearchParams(query)) {
    if (have.get(name) !== value) return step.route;
  }
  return null;
}

/**
 * Where to go before a manually started tour begins: its route, when that is a
 * concrete path and we aren't on it. Pattern routes can't be navigated to.
 */
export function tourEntryRoute(tour: TourDefinition, pathname: string): string | null {
  if (!tour.route || /[$*]/.test(tour.route)) return null;
  if (tour.steps[0]?.route) return null;
  return matchRoute(tour.route, pathname) ? null : tour.route;
}

export type StepOutcome =
  | { kind: 'show'; index: number }
  | { kind: 'skip'; next: number }
  | { kind: 'end'; completed: boolean };

/**
 * Decides what happens after trying to show step `index`.
 * `found` is whether its element turned up before the timeout.
 */
export function resolveStepOutcome(
  steps: TourStep[],
  index: number,
  direction: 1 | -1,
  found: boolean,
): StepOutcome {
  if (index < 0) return { kind: 'end', completed: false };
  if (index >= steps.length) return { kind: 'end', completed: true };
  if (found) return { kind: 'show', index };
  if (steps[index].optional) {
    const next = index + direction;
    if (next < 0) return { kind: 'end', completed: false };
    if (next >= steps.length) return { kind: 'end', completed: true };
    return { kind: 'skip', next };
  }
  return { kind: 'end', completed: false };
}

export interface AutoStartContext extends TourAudienceContext {
  flags: OnboardingFlags;
  modalOpen: boolean;
  tourActive: boolean;
}

export function isTourEligible(tour: TourDefinition, ctx: TourAudienceContext): boolean {
  if (tour.steps.length === 0) return false;
  return tour.audience ? tour.audience(ctx) : true;
}

/** The first tour that should start on its own here, if any. */
export function pickAutoStartTour(
  tours: TourDefinition[],
  ctx: AutoStartContext,
): TourDefinition | null {
  if (!ctx.isAuthenticated || ctx.modalOpen || ctx.tourActive) return null;
  return (
    tours.find(
      (tour) =>
        tour.autoStart !== false &&
        !!tour.route &&
        matchRoute(tour.route, ctx.pathname) &&
        !hasSeenTour(ctx.flags, tour.id, tour.version) &&
        isTourEligible(tour, ctx),
    ) ?? null
  );
}

export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

/** Pads the target rect and keeps it on screen. */
export function spotlightRect(target: Rect, viewport: Size, padding = 6): Rect {
  const left = Math.max(target.left - padding, 0);
  const top = Math.max(target.top - padding, 0);
  const right = Math.min(target.left + target.width + padding, viewport.width);
  const bottom = Math.min(target.top + target.height + padding, viewport.height);
  return { left, top, width: Math.max(right - left, 0), height: Math.max(bottom - top, 0) };
}

export interface CardPosition {
  top: number;
  left: number;
  placement: Exclude<TourPlacement, 'auto'> | 'center';
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Places the step card next to the spotlight, falling back through the other
 * sides when there isn't room, and centring it when nothing fits.
 */
export function placeCard(
  target: Rect | null,
  card: Size,
  viewport: Size,
  preferred: TourPlacement = 'auto',
  gap = 14,
  margin = 12,
): CardPosition {
  const centred: CardPosition = {
    top: clamp((viewport.height - card.height) / 2, margin, viewport.height - card.height - margin),
    left: clamp((viewport.width - card.width) / 2, margin, viewport.width - card.width - margin),
    placement: 'center',
  };
  if (!target) return centred;

  const space = {
    top: target.top - margin,
    bottom: viewport.height - (target.top + target.height) - margin,
    left: target.left - margin,
    right: viewport.width - (target.left + target.width) - margin,
  };
  const fits = {
    top: space.top >= card.height + gap,
    bottom: space.bottom >= card.height + gap,
    left: space.left >= card.width + gap,
    right: space.right >= card.width + gap,
  };

  const order: Array<keyof typeof fits> =
    preferred === 'auto'
      ? ['bottom', 'top', 'right', 'left']
      : [
          preferred,
          ...(['bottom', 'top', 'right', 'left'] as const).filter((p) => p !== preferred),
        ];
  const side = order.find((p) => fits[p]);
  if (!side) return centred;

  const centreX = target.left + target.width / 2 - card.width / 2;
  const centreY = target.top + target.height / 2 - card.height / 2;
  const maxLeft = viewport.width - card.width - margin;
  const maxTop = viewport.height - card.height - margin;

  switch (side) {
    case 'bottom':
      return {
        top: target.top + target.height + gap,
        left: clamp(centreX, margin, maxLeft),
        placement: 'bottom',
      };
    case 'top':
      return {
        top: target.top - gap - card.height,
        left: clamp(centreX, margin, maxLeft),
        placement: 'top',
      };
    case 'right':
      return {
        top: clamp(centreY, margin, maxTop),
        left: target.left + target.width + gap,
        placement: 'right',
      };
    case 'left':
      return {
        top: clamp(centreY, margin, maxTop),
        left: target.left - gap - card.width,
        placement: 'left',
      };
  }
}
